import { ToastContext } from "context/ToastContext";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import {
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  DraftVerdict,
  IExistingBaseRateFile,
  IDraftRecMatch,
  IDraftRecommendation,
  IDraftVerification,
  useAcceptContractModelDraftRecommendationMutation,
  useConfirmContractModelDraftDocumentMutation,
  useGetContractModelDraftRunQuery,
  useGetContractModelDraftExistingBaseRateFilesQuery,
  useGetContractModelDraftProviderHintQuery,
  useGetContractModelDraftVerificationQuery,
  useLazyGetContractModelDraftBaseRateFileCsvQuery,
  usePresignContractModelDraftDocumentMutation,
  useAddContractModelDraftMessageMutation,
  useDeleteContractModelDraftMessageMutation,
  useRegenerateContractModelDraftRunMutation,
  useRejectContractModelDraftRecommendationMutation,
  useRestoreContractModelDraftRecommendationMutation,
  useRunContractModelDraftGenerationMutation,
  useSetContractModelDraftRecommendationImplementedMutation,
} from "store/queries/contractModelDrafts";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";

import GenerationProgress from "./GenerationProgress";
import {
  ALLOWED_UPLOAD_EXTENSIONS,
  attributionBadge,
  canGenerateBaseRateFile,
  collectMissingInputs,
  comparisonSummary,
  componentTypeLabel,
  confidenceSeverity,
  firstPayerRulesHref,
  generateHint,
  generationBlockers,
  generationHelpText,
  groupByPayer,
  humanizeKey,
  buildSteps,
  buildStepsNote,
  isGeneratableAction,
  isOnChecklist,
  isProcessing,
  isRunFinished,
  LIVE_META_KEYS,
  looksLikeMissingDocument,
  missingInputsOf,
  openQuestionsOf,
  payerRulesHref,
  productionFileNames,
  recPayer,
  recStatusLabel,
  recSubtitle,
  reuseBaseRateFileIdToFill,
  runDisplayName,
  runStatusLabel,
  runStatusSeverity,
  siteTierLabel,
} from "./utils";
import AdjustmentRulesPanel from "./wizard/AdjustmentRulesPanel";
import ChangesScreen from "./wizard/ChangesScreen";
import InstructionPanel from "./wizard/InstructionPanel";
import LineOfBusinessPicker from "./wizard/LineOfBusinessPicker";
import PendingChangesBar from "./wizard/PendingChangesBar";
import ProposedModelingForm, {
  IPayerOption,
} from "./wizard/ProposedModelingForm";
import RateGrid from "./wizard/RateGrid";
import ServiceLinePicker from "./wizard/ServiceLinePicker";
import {
  recommendationsForStep,
  serviceLineOf,
  serviceLinesIn,
  stepBlocksAdvance,
  stepCanConfirm,
  stepForRecommendation,
  StepId,
  stepIsConfirmed,
  unlockedThroughIndex,
  unresolvedInStep,
  WIZARD_STEPS,
} from "./wizard/steps";

/** Ceiling on a single document upload. Contract PDFs/workbooks are small; a slow one is a broken one. */
const UPLOAD_TIMEOUT_MS = 120_000;

/** Pull a human-readable message out of an RTK Query error, a thrown Error, or anything else. */
const errorDetail = (e: unknown): string => {
  const apiMessage = (e as { data?: { message?: string } })?.data?.message;
  if (typeof apiMessage === "string" && apiMessage.length > 0)
    return apiMessage;
  if (e instanceof Error && e.message) return e.message;
  return "Could not upload the file.";
};

// resolve(key, value) may return a display name for an id-like value (e.g. payerId 20 → "Aetna").
type IdResolver = (key: string, value: unknown) => string | null;

// Long arrays (a carveout's ~60 CPT rows) collapse to a preview with a "show all" toggle so the card
// stays navigable.
const COLLAPSE_THRESHOLD = 8;
function CollapsibleRows({ children }: { children: ReactNode[] }) {
  const [expanded, setExpanded] = useState(false);
  if (children.length <= COLLAPSE_THRESHOLD)
    return <div className="flex flex-column gap-2">{children}</div>;
  const shown = expanded ? children : children.slice(0, COLLAPSE_THRESHOLD);
  return (
    <div className="flex flex-column gap-2">
      {shown}
      <Button
        className="p-button-text p-button-sm p-0 align-self-start"
        label={
          expanded
            ? "Show fewer"
            : `Show all ${children.length} (${
                children.length - COLLAPSE_THRESHOLD
              } more)`
        }
        onClick={() => setExpanded((v) => !v)}
      />
    </div>
  );
}

// Render one proposedModeling value: primitives inline, arrays/objects as nested readable rows — so the
// reviewer reads labeled fields instead of a horizontally-scrolling JSON blob. The label column is
// fit-to-content (grid `max-content 1fr`), so values sit next to their labels rather than far right.
function ProposedValue({
  value,
  fieldKey,
  resolve,
}: {
  value: unknown;
  fieldKey?: string;
  resolve?: IdResolver;
}) {
  if (value === null || value === undefined) {
    return <span className="text-700">&mdash;</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-700">(none)</span>;
    const allPrimitive = value.every(
      (v) => typeof v !== "object" || v === null
    );
    if (allPrimitive) {
      return (
        <ul className="mt-0 mb-0 pl-3">
          {value.map((v, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{String(v)}</li>
          ))}
        </ul>
      );
    }
    return (
      <CollapsibleRows>
        {value.map((v, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="surface-50 border-round p-2">
            <ProposedValue value={v} resolve={resolve} />
          </div>
        ))}
      </CollapsibleRows>
    );
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    // Collapse the payer/provider id+name pairs into a single readable row: "Payer  Aetna (4)" and
    // "Provider  HMA (Specialist Facility rates) (140f…)" — no separate id and name rows.
    const hasPayerId = Object.keys(obj).some(
      (k) => k.toLowerCase() === "payerid"
    );
    const entries = Object.entries(obj).filter(
      ([k]) =>
        !(hasPayerId && k.toLowerCase() === "payername") &&
        !LIVE_META_KEYS.includes(k)
    );
    const named = (
      label: string,
      name: string | null,
      id: unknown
    ): [string, ReactNode] => [
      label,
      <span style={{ overflowWrap: "anywhere" }}>
        {name ? `${name} (${String(id)})` : String(id)}
      </span>,
    ];
    return (
      // NOT PrimeFlex `.grid` — that class carries negative margins that pull the block up into any
      // inline text before it (e.g. the "It originally proposed:" line), which read as overlap.
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          columnGap: "1.5rem",
          rowGap: "0.25rem",
          alignItems: "start",
        }}
      >
        {entries.map(([k, v]) => {
          const kl = k.toLowerCase();
          let label = humanizeKey(k);
          let node: ReactNode = (
            <ProposedValue value={v} fieldKey={k} resolve={resolve} />
          );
          if (kl === "payerid") {
            let nm: string | null =
              typeof obj.payerName === "string" && obj.payerName.trim() !== ""
                ? obj.payerName
                : null;
            if (!nm && resolve) nm = resolve("payerId", v);
            [label, node] = named("Payer", nm, v);
          } else if (kl === "providerid") {
            [label, node] = named(
              "Provider",
              resolve ? resolve("providerId", v) : null,
              v
            );
          }
          return (
            <Fragment key={k}>
              <div className="font-medium text-700 white-space-nowrap">
                {label}
              </div>
              <div style={{ overflowWrap: "anywhere", minWidth: 0 }}>
                {node}
              </div>
            </Fragment>
          );
        })}
      </div>
    );
  }
  const name = fieldKey && resolve ? resolve(fieldKey, value) : null;
  return (
    <span style={{ overflowWrap: "anywhere" }}>
      {String(value)}
      {name ? <span className="text-700"> ({name})</span> : null}
    </span>
  );
}

function AttributionBadges({ rec }: { rec: IDraftRecommendation }) {
  if (rec.attributions.length === 0) {
    return <span className="text-700 text-sm">No source attribution</span>;
  }
  return (
    <div className="flex flex-column gap-3">
      {rec.attributions.map((a) => {
        const badge = attributionBadge(a);
        return (
          <div key={a.id} className="flex flex-column gap-1">
            {/* Icon + text, not a filled pill: a page with six loud red/green tags on it reads as an
                error state even when nothing is wrong. */}
            <span
              title={badge.tip}
              className="inline-flex align-items-center gap-1 text-sm font-medium"
              style={{ color: badge.color }}
            >
              <i
                className={`pi ${badge.icon}`}
                style={{ fontSize: "0.85rem" }}
              />
              {badge.label}
            </span>
            <blockquote className="m-0 pl-3 border-left-2 surface-border text-sm">
              <em>&ldquo;{a.exactQuote}&rdquo;</em>
            </blockquote>
            {a.fileName || a.locator ? (
              <span className="text-600 text-xs">
                Source: {[a.fileName, a.locator].filter(Boolean).join(" — ")}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// Per-recommendation verdict: how THIS rec lines up with what's already in production. Rendered as an
// icon + colored text (not a filled pill) so the confidence pill stays the only badge on a row, and so
// the states that need attention — amber "needs review", red "differs" — stand out from the calm ones.
const VERDICT_META: Record<
  string,
  { icon: string; color: string; label: string; tip: string }
> = {
  match: {
    icon: "pi-check-circle",
    color: "var(--green-600)",
    label: "Matches production",
    tip: "A matching construct exists in production and the value we can compare agrees.",
  },
  differs: {
    icon: "pi-ban",
    color: "var(--red-600)",
    label: "Differs from production",
    tip: "A matching construct exists, but a value we can compare disagrees with the proposal.",
  },
  present: {
    icon: "pi-eye",
    color: "var(--orange-600)",
    // Was "Needs review", which collided word-for-word with the review-status tag. This says what it
    // actually means: the thing exists in production, but we could not compare its values for you.
    label: "In production — check the values",
    tip: "A construct of this type exists for this provider & payer, but its exact values weren't auto-compared — confirm they match the proposal.",
  },
  missing: {
    icon: "pi-minus-circle",
    color: "var(--text-color-secondary)",
    label: "Not in production",
    tip: "Nothing in production corresponds to this recommendation yet — it still needs to be built.",
  },
};

function VerdictBadge({ verdict }: { verdict?: DraftVerdict }) {
  if (!verdict || verdict === "n_a") return null;
  const m = VERDICT_META[verdict];
  return (
    <span
      title={m.tip}
      className="inline-flex align-items-center gap-1 font-semibold text-sm white-space-nowrap"
      style={{ color: m.color }}
    >
      <i className={`pi ${m.icon}`} style={{ fontSize: "0.9rem" }} />
      {m.label}
    </span>
  );
}

// A deep link straight to the live construct in the Payer Rules editor, opened in a new tab so the
// checklist stays put. Shown only when the construct has a navigable page.
function PayerRulesLink({ href }: { href: string }) {
  return (
    <Link
      to={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex align-items-center gap-1 text-sm font-medium no-underline"
      style={{ color: "var(--primary-color)" }}
    >
      <i className="pi pi-external-link" style={{ fontSize: "0.75rem" }} />
      Check it out
    </Link>
  );
}

// The "In production" cell for one checklist row: the verdict plus a one-line, human-readable detail.
function ProductionCell({
  rec,
  match,
  loading,
}: {
  rec: IDraftRecommendation;
  match?: IDraftRecMatch | null;
  loading: boolean;
}) {
  const verdict = match?.verdict;
  if (!verdict) {
    return (
      <span className="text-700 text-sm">
        {loading ? (
          <>
            <i className="pi pi-spin pi-spinner mr-1" />
            Checking…
          </>
        ) : (
          "—"
        )}
      </span>
    );
  }
  if (verdict === "n_a") {
    return (
      <span
        className="text-700 text-sm"
        title="A scoping/routing decision with no single production construct to compare against."
      >
        Not applicable
      </span>
    );
  }

  const { proposed, production } = comparisonSummary(rec, match);
  let detail: ReactNode = null;
  if (verdict === "differs") {
    detail =
      proposed && production ? (
        <div className="text-sm mt-1">
          Proposed <strong>{proposed}</strong> &middot; Production{" "}
          <strong>{production}</strong>
        </div>
      ) : (
        <div className="text-700 text-sm mt-1">
          See the recommendation below for the difference.
        </div>
      );
  } else if (verdict === "match") {
    detail = production ? (
      <div className="text-700 text-sm mt-1">Production: {production}</div>
    ) : null;
  } else if (verdict === "present") {
    const files = productionFileNames(match);
    detail =
      files.length > 0 ? (
        <div className="text-700 text-sm mt-1">
          A base rate file for this provider &amp; payer already exists (
          <strong>{files.join(", ")}</strong>), but the proposal names a
          different one &mdash; we haven&rsquo;t verified it&rsquo;s the same
          file. Confirm below.
        </div>
      ) : (
        <div className="text-700 text-sm mt-1">
          Exists in production, but its values weren&rsquo;t auto-checked
          &mdash; confirm they match below.
        </div>
      );
  } else if (verdict === "missing") {
    detail = (
      <div className="text-700 text-sm mt-1">Nothing built for this yet.</div>
    );
  }

  const href = firstPayerRulesHref(rec.componentType, match?.rows);
  return (
    <div>
      <div className="flex align-items-center flex-wrap gap-3">
        <VerdictBadge verdict={verdict} />
        {href ? <PayerRulesLink href={href} /> : null}
      </div>
      {detail}
    </div>
  );
}

// The base rate files a live contract already prices this provider+payer against — surfaced so the
// reviewer can reuse one instead of uploading a freshly generated file. Deliberately coarse (names +
// row counts, not an exact content diff): a byte-for-byte match against a hand-built production file
// almost never lands, and "here's what already exists" is the more honest, useful signal.
function ExistingBaseRateFiles({ files }: { files: IExistingBaseRateFile[] }) {
  if (files.length === 0) return null;
  return (
    <div className="mb-3 surface-50 border-round p-3">
      <div className="text-sm font-medium mb-1">
        A base rate file for this provider &amp; payer may already exist
      </div>
      <div className="text-600 text-xs mb-2">
        These are already loaded for this payer &amp; provider — you may be able
        to reference one instead of uploading a new file. Compare its row count
        to the file this would generate as a rough check.
      </div>
      <ul className="mt-0 mb-0 pl-3 text-sm text-700">
        {files.map((f) => (
          <li key={f.displayName} className="mb-1">
            <span className="font-medium">{f.displayName}</span>
            {f.rowCount != null ? ` — ${f.rowCount.toLocaleString()} rows` : ""}
            {f.effectiveDate ? ` (eff ${f.effectiveDate})` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecommendationCard({
  rec,
  onAccept,
  onReject,
  onOverride,
  busy,
  resolve,
  payers,
  baseRateFiles,
  baseRateFileOptions,
  existingBaseRateFiles = [],
  match,
  focused,
  onGenerateCsv,
  generatingCsv,
}: {
  rec: IDraftRecommendation;
  /** `edited` is sent only when the reviewer actually changed something. */
  onAccept: (edited?: Record<string, unknown>) => void;
  onReject: () => void;
  onOverride: () => void;
  busy: boolean;
  resolve?: IdResolver;
  payers?: IPayerOption[];
  /** Base rate file display names for this account — options for the "file to take a % of" picker. */
  baseRateFiles?: string[];
  /** Loaded files as {id, displayName} — so a reuse_existing pick writes both name and id. */
  baseRateFileOptions?: { id: string; displayName: string }[];
  /** Base rate files already in production for this run's provider+payer — a possible reuse. */
  existingBaseRateFiles?: IExistingBaseRateFile[];
  match?: IDraftRecMatch | null;
  focused?: boolean;
  onGenerateCsv?: (
    recId: string,
    proposedModeling?: Record<string, unknown>
  ) => void;
  generatingCsv?: boolean;
}) {
  const proposed = useMemo(
    () => (rec.proposedModeling ?? {}) as Record<string, unknown>,
    [rec.proposedModeling]
  );
  const [draft, setDraft] = useState<Record<string, unknown>>(proposed);

  // Re-seed when the server sends a new value (accept/refetch), but never while the reviewer is
  // mid-edit on this card — clobbering their typing would be maddening.
  useEffect(() => {
    setDraft(proposed);
  }, [proposed]);

  // The recommendation as it stands with unsaved edits — so the generate button reflects what you're
  // editing RIGHT NOW (enabled/blocked live), and generates from the draft without needing a save first.
  const draftRec = useMemo(
    () => ({ ...rec, proposedModeling: draft }),
    [rec, draft]
  );

  // Keep a reuse_existing file's required `baseRateFileId` in step with the chosen NAME. The id gets
  // stripped whenever the rec is saved under another action, so switching back to "reuse" can leave a
  // name with no id — looks selected, but won't save. Re-derive the missing id from the loaded-file list.
  useEffect(() => {
    const fill = reuseBaseRateFileIdToFill(draft, baseRateFileOptions ?? []);
    if (fill) setDraft((d) => ({ ...d, baseRateFileId: fill }));
  }, [draft, baseRateFileOptions]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(proposed),
    [draft, proposed]
  );

  const errors = rec.validationErrors ?? null;
  const hasErrors = (errors?.length ?? 0) > 0;

  // Current verdict, so the buttons can show a selected state on a card you're revisiting.
  const accepted = rec.status === "accepted";
  const rejected = rec.status === "rejected";

  return (
    <div
      id={`rec-${rec.id}`}
      style={{
        scrollMarginTop: "1rem",
        borderRadius: "6px",
        transition: "box-shadow 0.3s ease",
        boxShadow: focused ? "0 0 0 2px var(--primary-color)" : "none",
      }}
    >
      <Card className="mb-3">
        <div className="flex justify-content-between align-items-start gap-2 mb-2">
          <div className="flex align-items-center flex-wrap gap-2">
            <span className="font-bold">
              {componentTypeLabel(rec.componentType)}
            </span>
            {recPayer(rec).name ? (
              <Tag
                className="white-space-nowrap"
                icon="pi pi-building"
                value={recPayer(rec).name ?? ""}
              />
            ) : null}
            <Tag
              severity={confidenceSeverity(rec.confidence)}
              value={`${rec.confidence} confidence`}
            />
            {/* Only worth a tag once she has ACTED on it. "Needs review" on every untouched card is
                noise — the Confirm/Reject buttons already say that, and it collided with the verdict. */}
            {rec.status !== "pending" ? (
              <Tag value={recStatusLabel(rec.status)} />
            ) : null}
            {/* The production verdict is NOT shown here — the "In production now" column below says the
                same thing in full, and a header badge repeating it was pure duplication. */}
          </div>
        </div>
        {/* For a base rate file, show what already exists for this provider+payer — the reviewer may be
            able to reuse one instead of uploading a new file. */}
        {rec.componentType === "base_rate_file" ? (
          <ExistingBaseRateFiles files={existingBaseRateFiles} />
        ) : null}
        {hasErrors ? (
          <div className="mb-3 p-3 border-round border-1 border-orange-500 surface-50">
            <div className="text-sm font-semibold mb-1">
              <i className="pi pi-exclamation-triangle mr-1" />
              The agent&rsquo;s values don&rsquo;t fit a{" "}
              {componentTypeLabel(rec.componentType)}
            </div>
            <div className="text-sm text-700">
              Correct the highlighted fields below, then confirm. Until this is
              fixed we can&rsquo;t tell you how to build it, and we can&rsquo;t
              check it against production.
            </div>
          </div>
        ) : null}

        {rec.originalModeling ? (
          <div className="mb-3 surface-50 p-2 border-round text-xs">
            {/* Label on its own line so the value grid below starts clean — a block grid inline after
                text was where the overlap showed. */}
            <div className="mb-2">
              <span className="font-semibold">You corrected the agent.</span> It
              originally proposed:
            </div>
            <ProposedValue value={rec.originalModeling} resolve={resolve} />
          </div>
        ) : null}

        <div className="grid">
          <div className={match ? "col-12 md:col-6" : "col-12"}>
            <div className="text-sm font-semibold mb-1">Proposed modeling</div>
            <div className="surface-100 p-3 border-round text-sm h-full">
              <ProposedModelingForm
                componentType={rec.componentType}
                value={draft}
                validationErrors={errors}
                disabled={busy}
                // So payerId 4 renders as "Aetna (4)" instead of a bare number the reviewer can't read.
                resolve={resolve}
                payers={payers}
                baseRateFiles={baseRateFiles}
                baseRateFileOptions={baseRateFileOptions}
                onChange={setDraft}
              />
            </div>
          </div>
          {match ? (
            <div className="col-12 md:col-6">
              <div className="text-sm font-semibold mb-1">
                In production now
              </div>
              <div className="surface-50 p-3 border-round text-sm h-full">
                {match.rows.length === 0 ? (
                  <span className="text-700">
                    No matching {componentTypeLabel(rec.componentType)} found
                    for this provider &amp; payer.
                  </span>
                ) : (
                  <div className="flex flex-column gap-2">
                    {match.rows.map((row, i) => {
                      const href = payerRulesHref(rec.componentType, row);
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} className="surface-0 border-round p-2">
                          <ProposedValue value={row} resolve={resolve} />
                          {href ? (
                            <div className="mt-2">
                              <PayerRulesLink href={href} />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
                {match.verdict === "present" && match.rows.length > 0 ? (
                  <div className="text-600 text-xs mt-2">
                    A matching construct exists; its values weren&rsquo;t
                    compared automatically &mdash; confirm they match.
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {missingInputsOf(rec).length > 0 ? (
          <div className="mt-3">
            <div className="text-sm font-semibold mb-1">
              Blocked &mdash; can&rsquo;t model this without
            </div>
            <ul className="mt-0 mb-0 pl-3 text-sm text-700">
              {missingInputsOf(rec).map((item) => (
                <li key={item} className="mb-1">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {openQuestionsOf(rec).length > 0 ? (
          <div className="mt-3">
            <div className="text-sm font-semibold mb-1">
              Open questions &mdash; modeled anyway, please confirm
            </div>
            <ul className="mt-0 mb-0 pl-3 text-sm text-600">
              {openQuestionsOf(rec).map((item) => (
                <li key={item} className="mb-1">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-3">
          <div className="text-sm font-semibold mb-1">Source language</div>
          <AttributionBadges rec={rec} />
        </div>
        {rec.override ? (
          <div className="mt-2 surface-50 p-2 border-round text-sm">
            <span className="font-semibold">
              Feedback ({rec.override.category}):
            </span>{" "}
            {rec.override.reason}
          </div>
        ) : null}
        {/* Generate & download lives right above Save, and runs off the CURRENT (unsaved) draft — so the
            loop is edit → generate → eyeball the CSV → Save & confirm, not save-first-then-scroll-up.
            Shown for any generate-type action; disabled with a reason until the spec is complete. */}
        {onGenerateCsv &&
        (isGeneratableAction(draftRec) || canGenerateBaseRateFile(draftRec)) ? (
          <div className="mt-3 surface-50 border-round p-3">
            <Button
              label="Generate & download base rate CSV"
              icon="pi pi-download"
              className="p-button-sm p-button-outlined"
              loading={generatingCsv}
              disabled={!canGenerateBaseRateFile(draftRec) || busy}
              onClick={() => onGenerateCsv(rec.id, draft)}
            />
            <div className="text-600 text-xs mt-2">
              {canGenerateBaseRateFile(draftRec) ? (
                <>
                  {generationHelpText(draftRec)} Previews from your current
                  edits &mdash; no need to save first. Then upload it as a base
                  rate file in Payer Rules and reference it from the rate
                  calculation.
                </>
              ) : (
                <>
                  Add {generationBlockers(draftRec).join(", ")} above, then this
                  button will generate the file (CPT &rarr; rate) for you to
                  download.
                </>
              )}
            </div>
          </div>
        ) : null}
        <div className="flex gap-2 mt-3 align-items-center">
          <Button
            // The buttons show the CURRENT verdict as a selected state, so you can see what you already
            // did on a card you're revisiting. Confirmed → solid green with a filled check; otherwise a
            // plain Confirm. Both stay clickable so you can change your mind.
            label={
              // eslint-disable-next-line no-nested-ternary
              dirty ? "Save & confirm" : accepted ? "Confirmed" : "Confirm"
            }
            icon={
              // eslint-disable-next-line no-nested-ternary
              dirty
                ? "pi pi-save"
                : accepted
                ? "pi pi-check-circle"
                : "pi pi-check"
            }
            className={`p-button-sm ${accepted ? "p-button-success" : ""}`}
            tooltip="Marks this as correct and adds it to the Build checklist. Immediate — no rebuild."
            tooltipOptions={{ position: "top" }}
            onClick={() => onAccept(dirty ? draft : undefined)}
            disabled={busy}
          />
          <Button
            label={rejected ? "Rejected" : "Reject"}
            icon={rejected ? "pi pi-times-circle" : undefined}
            className={`p-button-sm p-button-danger ${
              rejected ? "" : "p-button-outlined"
            }`}
            tooltip="Skip this — it won't be built. Reversible."
            tooltipOptions={{ position: "top" }}
            onClick={onReject}
            disabled={busy}
          />
          <Button
            // Same mechanism as the step-level "Requested changes" box — this one is just pre-scoped to
            // this recommendation. Both queue an instruction the agent applies on the next Regenerate.
            label="Request a change"
            icon="pi pi-comment"
            className="p-button-sm p-button-text"
            tooltip="Queue an instruction for the agent, applied on the next Regenerate. Doesn't change this now."
            tooltipOptions={{ position: "top" }}
            onClick={onOverride}
            disabled={busy}
          />
          {dirty ? (
            <small className="text-color-secondary ml-auto">
              Unsaved changes
            </small>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

/**
 * The wizard's left rail. Steps lock: you may move through the ones you have settled and the one you
 * are settling, and no further (Ani's v0 — "Confirm & continue", padlocks on what's ahead).
 *
 * An earlier build deliberately left every step clickable, reasoning that a reviewer wants to skim
 * ahead. We've taken her design instead — the sequence is the method, and reviewing outpatient rates
 * before you've settled the contract's effective date invites exactly the rework the order prevents.
 *
 * Steps already passed stay open, so going *back* to fix something is always allowed. Only what's ahead
 * of the gate is locked, and the lock is on `pending` items alone — see `stepBlocksAdvance`.
 */
function StepRail({
  recs,
  activeStep,
  onSelect,
  unlockedThrough,
  showChanges,
  hasServiceLines,
  hasSegments,
}: {
  recs: IDraftRecommendation[];
  activeStep: StepId;
  onSelect: (step: StepId) => void;
  /** Index into WIZARD_STEPS; anything beyond this is locked. */
  unlockedThrough: number;
  /** "What Changed" is a version-diff — meaningless until there has been a second version. */
  showChanges: boolean;
  /** The Service Lines step is only meaningful when the contract actually has service lines to pick
   * from. On a professional/single-schedule contract nothing is service-line-scoped, so the step would
   * be an empty dead click — hide it. */
  hasServiceLines: boolean;
  /** Lines of Business is only meaningful when the contract spans more than one payer; hide it otherwise. */
  hasSegments: boolean;
}) {
  return (
    <nav className="flex flex-column gap-1">
      {WIZARD_STEPS.filter(
        (s) =>
          s.id !== "documents" &&
          (showChanges || s.id !== "changes") &&
          (hasSegments || s.id !== "segments") &&
          (hasServiceLines || s.id !== "service_lines")
      ).map((step) => {
        const outstanding = unresolvedInStep(recs, step.id);
        const confirmed =
          stepCanConfirm(step.id) && stepIsConfirmed(recs, step.id);
        const isActive = step.id === activeStep;
        const locked =
          WIZARD_STEPS.findIndex((s) => s.id === step.id) > unlockedThrough;
        return (
          <button
            key={step.id}
            type="button"
            disabled={locked}
            title={
              locked
                ? "Confirm the steps before this one to continue"
                : undefined
            }
            onClick={() => onSelect(step.id)}
            className={`text-left border-round p-3 w-full border-none ${
              locked ? "cursor-auto opacity-50" : "cursor-pointer"
            } ${isActive ? "text-primary font-medium" : "surface-card"}`}
            style={
              isActive
                ? {
                    // A grey tint was invisible against the page's grey. A clear primary-tinted fill
                    // plus a solid accent bar on the leading edge is unmistakable at a glance, which is
                    // the whole job of a rail.
                    background: "var(--primary-50, #eef2ff)",
                    borderLeft: "4px solid var(--primary-color)",
                    boxShadow: "inset 0 0 0 1px var(--primary-200, #c7d2fe)",
                  }
                : { borderLeft: "4px solid transparent" }
            }
          >
            <div className="flex align-items-center gap-2">
              {/* eslint-disable-next-line no-nested-ternary */}
              {locked ? (
                <i className="pi pi-lock text-color-secondary" />
              ) : confirmed ? (
                <i className="pi pi-check-circle text-green-600" />
              ) : (
                <i className="pi pi-circle text-color-secondary" />
              )}
              <span
                className={`text-sm ${isActive ? "font-bold" : "font-medium"}`}
              >
                {step.title}
              </span>
              {outstanding > 0 ? (
                <Tag
                  className="ml-auto"
                  severity="warning"
                  value={String(outstanding)}
                />
              ) : null}
            </div>
          </button>
        );
      })}
    </nav>
  );
}

function ReviewExplainer({ count }: { count: number }) {
  return (
    <div className="surface-50 border-1 surface-border border-round p-3 mb-3 text-sm">
      <div className="font-semibold mb-1">How to review this</div>
      <p className="mt-0 mb-2 text-700 line-height-3">
        The model read your documents and proposed {count} pieces of a
        ClearHealth pricing configuration. Nothing here is live — reviewing it{" "}
        <strong>never changes production pricing</strong>. Your job is to say,
        for each piece, whether the model read the contract correctly.
      </p>
      <ul className="mt-0 mb-0 pl-3 text-700">
        <li className="mb-1">
          Check the <strong>source language</strong> quoted under each proposal
          &mdash; against the contract itself. Most quotes are checked against
          the text we extracted from your documents; a few can&rsquo;t be
          matched automatically (scanned pages and spreadsheets are hard to
          match), and those are flagged &mdash; a flag means &ldquo;we
          couldn&rsquo;t verify it&rdquo;, not that the quote is wrong.
        </li>
        <li className="mb-1">
          <strong>Confirm</strong> marks the proposal as agreed, so it appears
          on the Build checklist. <strong>Request a change</strong> queues a
          note for the agent to apply on the next <strong>Regenerate</strong>{" "}
          &mdash; it doesn&rsquo;t change this proposal now.{" "}
          <strong>Reject</strong> drops it from the checklist. None of the three
          write anything to production pricing.
        </li>
        <li>
          Low-confidence proposals and anything listed above as still needed are
          the places to look first.
        </li>
      </ul>
    </div>
  );
}

function MissingInputsPanel({ items }: { items: string[] }) {
  const documents = items.filter(looksLikeMissingDocument);
  const other = items.filter((i) => !looksLikeMissingDocument(i));
  return (
    <div
      className="surface-card border-1 border-round p-3 mb-4"
      style={{ borderColor: "var(--orange-500)" }}
    >
      <div className="flex align-items-center gap-2 mb-2">
        <i className="pi pi-exclamation-triangle text-orange-500" />
        <span className="font-semibold">
          Modeling is blocked &mdash; {items.length} required input
          {items.length === 1 ? "" : "s"} not found in the documents or in
          ClearHealth
        </span>
      </div>
      <p className="text-700 text-sm mt-0 mb-3">
        These are hard blockers, not caveats &mdash; the affected components
        could not be modeled at all. Gather them, then start a{" "}
        <strong>new run</strong> with the fuller set; this run stays as a
        record. Softer &ldquo;please confirm&rdquo; items appear as{" "}
        <strong>open questions</strong> on the individual proposals below.
      </p>

      {documents.length > 0 ? (
        <div className="mb-3">
          <div className="font-medium text-sm mb-1">Documents to provide</div>
          <ul className="mt-0 mb-0 pl-3 text-sm">
            {documents.map((d) => (
              <li key={d} className="mb-1">
                {d}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {other.length > 0 ? (
        <div>
          <div className="font-medium text-sm mb-1">
            Other information needed
          </div>
          <ul className="mt-0 mb-0 pl-3 text-sm">
            {other.map((o) => (
              <li key={o} className="mb-1">
                {o}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The implementation to-do list, as a two-column table: the left column is what to build (name,
 * confidence, how-to), the right column is how it compares to what's already in production for this
 * provider × payer (auto-loaded — verdict plus a one-line detail). Nothing here writes to production.
 */
function ImplementationChecklist({
  recs,
  verification,
  loadingLive,
  onRefresh,
  onToggle,
  onViewDetails,
}: {
  recs: IDraftRecommendation[];
  verification: IDraftVerification | null;
  loadingLive: boolean;
  onRefresh: () => void;
  onToggle: (recId: string, implemented: boolean) => void;
  onViewDetails: (recId: string) => void;
}) {
  const onChecklist = recs.filter(isOnChecklist);
  const groups = groupByPayer(onChecklist);
  const doneCount = onChecklist.filter((r) => r.implementedAt).length;
  const multiPayer = groups.filter((g) => g.payerId != null).length > 1;

  return (
    <div
      id="implementation-checklist"
      className="surface-card border-1 surface-border border-round p-3 mb-4"
    >
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <span className="font-semibold text-lg">Implementation checklist</span>
        <div className="flex align-items-center gap-3">
          <span className="text-700 text-sm">
            {doneCount}/{onChecklist.length} marked done
          </span>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            className="p-button-sm p-button-text"
            loading={loadingLive}
            onClick={onRefresh}
          />
        </div>
      </div>
      <ReviewExplainer count={recs.length} />
      <p className="text-700 text-sm mt-0 mb-3 line-height-3">
        Build each item in the Payer Rules UI, then tick it off. The{" "}
        <strong>In production</strong> column shows what&rsquo;s already in the
        database for this provider &amp; payer so you can confirm each value
        matches (you judge equivalence &mdash; e.g. a precomputed base file vs.
        layered percentages can be the same thing). Hit <strong>Refresh</strong>{" "}
        after you build something to re-check.
        {multiPayer ? (
          <>
            {" "}
            This contract covers{" "}
            <strong>
              {groups.filter((g) => g.payerId != null).length} payers
            </strong>{" "}
            — each row is grouped under the payer it&rsquo;s for.
          </>
        ) : null}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr className="text-left text-700 text-sm">
              <th className="font-medium py-2 pr-4" style={{ width: "58%" }}>
                To build
              </th>
              <th className="font-medium py-2">In production</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.key}>
                <tr>
                  <td colSpan={2} className="pt-4 pb-1">
                    <span className="text-xs uppercase font-semibold text-600 tracking-wide flex align-items-center gap-2">
                      <i
                        className={
                          group.payerId != null
                            ? "pi pi-building text-700"
                            : "pi pi-sitemap text-700"
                        }
                        style={{ fontSize: "0.8rem" }}
                      />
                      {group.payerId != null
                        ? `${group.payerName ?? "Payer"} (${group.payerId})`
                        : "Contract-wide (all payers)"}
                    </span>
                  </td>
                </tr>
                {group.recs.map((rec) => {
                  const subtitle = recSubtitle(rec);
                  return (
                    <tr key={rec.id} className="border-top-1 surface-border">
                      <td className="py-3 pr-4 align-top">
                        <div className="flex gap-3 align-items-start">
                          <Checkbox
                            inputId={`impl-${rec.id}`}
                            checked={!!rec.implementedAt}
                            onChange={(e) => onToggle(rec.id, !!e.checked)}
                            className="mt-1"
                          />
                          <div style={{ minWidth: 0 }}>
                            <div className="flex flex-wrap align-items-center gap-2 mb-1">
                              <span className="font-semibold">
                                {componentTypeLabel(rec.componentType)}
                              </span>
                              <Tag
                                severity={confidenceSeverity(rec.confidence)}
                                value={`${rec.confidence} confidence`}
                              />
                            </div>
                            {subtitle ? (
                              <div className="text-sm text-700 font-medium mb-1">
                                {subtitle}
                              </div>
                            ) : null}
                            {/* Concrete, value-filled steps — what to type, not just what to make.
                                "View details" still jumps to the full editable form below. */}
                            <ol className="text-sm text-700 mt-1 mb-0 pl-3 line-height-3">
                              {buildSteps(rec).map((step, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <li key={i} className="mb-1">
                                  {step}
                                </li>
                              ))}
                            </ol>
                            {buildStepsNote(rec) ? (
                              <div className="text-sm text-600 mt-2 p-2 surface-50 border-round line-height-3">
                                <span className="font-medium">Note: </span>
                                {buildStepsNote(rec)}
                              </div>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => onViewDetails(rec.id)}
                              className="inline-flex align-items-center gap-1 text-sm font-medium no-underline p-0 mt-2 bg-transparent border-none cursor-pointer"
                              style={{ color: "var(--primary-color)" }}
                            >
                              <i
                                className="pi pi-arrow-down"
                                style={{ fontSize: "0.75rem" }}
                              />
                              View / edit fields
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 align-top">
                        <ProductionCell
                          rec={rec}
                          match={verification?.[rec.id]}
                          loading={loadingLive && !verification}
                        />
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ContractModelDraftDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toastRef = useContext(ToastContext);

  // Poll ONLY while the agents-app worker is processing so the UI reflects progress. Once the run is
  // finished, stop — re-fetching the whole run (with all attributions) every 4s is wasteful. Starts at
  // 4s so an in-flight run is caught on first load, then the effect below turns it off when it settles.
  const [pollMs, setPollMs] = useState(4000);
  const { data: run } = useGetContractModelDraftRunQuery(id, {
    skip: !id,
    pollingInterval: pollMs,
  });
  useEffect(() => {
    // Unknown (still loading) → keep polling; known-and-processing → poll; settled → stop.
    setPollMs(!run || isProcessing(run.status) ? 4000 : 0);
  }, [run]);

  const [presignDoc] = usePresignContractModelDraftDocumentMutation();
  const [confirmDoc] = useConfirmContractModelDraftDocumentMutation();
  const [runGeneration, { isLoading: generating }] =
    useRunContractModelDraftGenerationMutation();
  // The reviewer's steering for this run, seeded from what was stored and editable before generating.
  const [instructions, setInstructions] = useState("");
  const seededRunIdRef = useRef<string | null>(null);
  // Seed the box from the stored value ONCE per run — but never clobber what the reviewer is typing.
  // Polling refreshes `run` every few seconds; re-seeding on every poll would erase mid-edit keystrokes.
  useEffect(() => {
    if (run && seededRunIdRef.current !== run.id) {
      seededRunIdRef.current = run.id;
      setInstructions(run.generationInstructions ?? "");
    }
  }, [run]);
  const [accept, { isLoading: accepting }] =
    useAcceptContractModelDraftRecommendationMutation();
  const [reject, { isLoading: rejecting }] =
    useRejectContractModelDraftRecommendationMutation();
  const [restore, { isLoading: restoring }] =
    useRestoreContractModelDraftRecommendationMutation();
  const [addMessage] = useAddContractModelDraftMessageMutation();
  const [deleteMessage] = useDeleteContractModelDraftMessageMutation();
  const [regenerate, { isLoading: regenerating }] =
    useRegenerateContractModelDraftRunMutation();
  const [setImplemented] =
    useSetContractModelDraftRecommendationImplementedMutation();
  const busy = accepting || rejecting || restoring;

  // Production comparison, auto-loaded (no toggle) once the run has recommendations, and re-pullable
  // via Refresh after the user builds something. Keyed by recommendationId; null until first loaded.
  const {
    data: verification = null,
    isFetching: loadingLive,
    refetch: refetchVerification,
  } = useGetContractModelDraftVerificationQuery(id, {
    skip: !id || !run?.recommendations?.length,
  });

  // "This contract looks modeled under a different provider" — only returns siblings when the run's own
  // provider+payer has nothing, so a non-empty list is a strong wrong-provider signal.
  const { data: providerHint } = useGetContractModelDraftProviderHintQuery(id, {
    skip: !id || !run?.recommendations?.length,
  });

  // Base rate files already loaded for this provider+payer — shown on base_rate_file cards as a
  // possible reuse. One run-level fetch, shared by every card via RTK cache.
  const { data: existingBrf } =
    useGetContractModelDraftExistingBaseRateFilesQuery(id, {
      skip: !id || !run?.recommendations?.length,
    });
  const existingBaseRateFiles = existingBrf?.files ?? [];

  // Payer/provider name lookups so proposedModeling shows "20 (Aetna Medicare Advantage)" not a bare id.
  const { data: payersData } = useGetAllPayersQuery();
  const payerOptions: IPayerOption[] = useMemo(
    () =>
      (
        (payersData?.data ?? []) as {
          id: number;
          name: string;
          type?: string;
        }[]
      )
        .map((p) => ({ id: p.id, name: p.name, type: p.type ?? null }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [payersData]
  );

  // The base rate files loaded for this account, so "File to take a % of" is a pick-list of real files
  // instead of free text — the source of the "two files named X" ambiguity the reviewer hit.
  const { data: baseRateFilesData } = useGetAllBaseRateFilesQuery(
    { accountId: run?.accountId },
    { skip: !run?.accountId }
  );
  const baseRateFileNames: string[] = useMemo(() => {
    const names = (baseRateFilesData?.data ?? [])
      .map((f) => f.displayName)
      .filter((n): n is string => typeof n === "string" && n.trim() !== "");
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [baseRateFilesData]);
  // {id, displayName} per file, deduped to the newest version of each name — so a reuse_existing pick
  // writes a valid id alongside the name (resolution downstream still lands on the latest version).
  const baseRateFileOptions = useMemo(() => {
    const byName = new Map<
      string,
      { id: string; displayName: string; version: number }
    >();
    (baseRateFilesData?.data ?? []).forEach((f) => {
      if (typeof f.displayName !== "string" || f.displayName.trim() === "")
        return;
      const prev = byName.get(f.displayName);
      if (!prev || (f.version ?? 0) > prev.version) {
        byName.set(f.displayName, {
          id: f.id,
          displayName: f.displayName,
          version: f.version ?? 0,
        });
      }
    });
    return Array.from(byName.values()).map((f) => ({
      id: f.id,
      displayName: f.displayName,
    }));
  }, [baseRateFilesData]);
  const { data: providersData } = useGetAllProvidersQuery();
  const resolveIdName = useMemo(() => {
    const payerNames = new Map<string, string>(
      ((payersData?.data ?? []) as { id: number; name: string }[]).map((p) => [
        String(p.id),
        p.name,
      ])
    );
    const providerNames = new Map<string, string>(
      (providersData?.data ?? []).map((p) => [p.id, p.name])
    );
    return (key: string, value: unknown): string | null => {
      const k = key.toLowerCase();
      if (k.endsWith("payerid")) return payerNames.get(String(value)) ?? null;
      if (k.endsWith("providerid"))
        return providerNames.get(String(value)) ?? null;
      return null;
    };
  }, [payersData, providersData]);

  // Which recommendation card to briefly ring after a "View details" jump, so the eye lands on it.
  const [focusRecId, setFocusRecId] = useState<string | null>(null);
  const scrollToId = (elementId: string) =>
    document
      .getElementById(elementId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  const [fetchBaseRateCsv] = useLazyGetContractModelDraftBaseRateFileCsvQuery();
  const [generatingCsvId, setGeneratingCsvId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Name of the file currently uploading, or null. Doubles as the progress indicator's label.
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const uploading = uploadingFile !== null;
  const [overrideTarget, setOverrideTarget] =
    useState<IDraftRecommendation | null>(null);
  const [overrideReason, setOverrideReason] = useState("");

  const allRecs = useMemo(() => run?.recommendations ?? [], [run]);

  // ---- Wizard -------------------------------------------------------------------------------------
  const [activeStep, setActiveStep] = useState<StepId>("terms");
  const activeStepMeta =
    WIZARD_STEPS.find((s) => s.id === activeStep) ?? WIZARD_STEPS[1];
  const stepIndex = WIZARD_STEPS.findIndex((s) => s.id === activeStep);
  const stepRecs = useMemo(
    () => recommendationsForStep(allRecs, activeStep),
    [allRecs, activeStep]
  );
  // Pass ALL recs as context so a base rate file (which carries no payer of its own) can be grouped under
  // the payer of the rate calc that references it — otherwise two payers' files pile into "Contract-wide".
  const stepPayerGroups = useMemo(
    () => groupByPayer(stepRecs, allRecs),
    [stepRecs, allRecs]
  );

  /**
   * How far the reviewer has unlocked. Held as a high-water mark that only moves forward.
   *
   * A regeneration resets modified recommendations to `pending`, which can make an *already-passed*
   * step start blocking again. Recomputing the gate from scratch would then relegate the reviewer to
   * an earlier step mid-review and lose their place, for a change they haven't even read yet. The
   * Changes screen is what surfaces that a regeneration moved something; the lock stays put.
   */
  const [reachedIndex, setReachedIndex] = useState(0);
  const unlockedThrough = useMemo(
    () => unlockedThroughIndex(allRecs, reachedIndex),
    [allRecs, reachedIndex]
  );
  const currentStepBlocks = stepBlocksAdvance(allRecs, activeStep);

  // Passing a step is what advances the mark; it never retreats on its own.
  useEffect(() => {
    if (stepIndex > reachedIndex) setReachedIndex(stepIndex);
  }, [stepIndex, reachedIndex]);
  /**
   * The two steps that hold a rate sheet render as a grid instead of cards. Grouping there is by
   * SERVICE LINE (the grid's own concern), not by payer — a run is one payer anyway, so the payer
   * accordion buys nothing on these steps and costs the at-a-glance view the grid exists to give.
   */
  const isGridStep = activeStep === "inpatient" || activeStep === "outpatient";
  /** Adjustment Rules reads as sentences with switches (v0 step 5), not typed cards. */
  const isRulesStep = activeStep === "adjustment_rules";

  /**
   * Jump to a recommendation from the checklist or the Changes screen.
   *
   * Since the wizard split the page into steps, the recommendation is almost never ON the step you are
   * looking at — so the old "scroll to it" did nothing at all. Switch to its step first, then scroll.
   * (The timeout lets the step render before we look for the anchor.)
   */
  const handleViewDetails = (recId: string) => {
    const rec = allRecs.find((r) => r.id === recId);
    if (rec) setActiveStep(stepForRecommendation(rec));
    setFocusRecId(recId);
    window.setTimeout(() => scrollToId(`rec-${recId}`), 120);
    window.setTimeout(
      () => setFocusRecId((cur) => (cur === recId ? null : cur)),
      2400
    );
  };
  const unresolvedHere = unresolvedInStep(allRecs, activeStep);

  // ---- Service-line picker ------------------------------------------------------------------------
  // Seeded from the run: a line is "selected" unless every recommendation in it is already rejected —
  // so the picker reflects reality on reload rather than resetting to all-selected.
  const availableLines = useMemo(() => serviceLinesIn(allRecs), [allRecs]);
  // The payer segments (lines of business) this contract covers — contract-wide recs carry no payer and
  // are not a segment you can decline, so they're excluded (same as no-service-line recs in the SL picker).
  const segments = useMemo(
    () => groupByPayer(allRecs).filter((g) => g.payerId != null),
    [allRecs]
  );

  // Steps hidden from the rail must also be skipped by Back / Confirm & continue, or the linear nav
  // would land on an empty page. The rail filters: the upload screen, "What Changed" until there's a
  // second version, Lines of Business unless the contract spans more than one, and Service Lines when
  // the contract has none.
  const stepHidden = useCallback(
    (s: StepId): boolean => {
      if (s === "documents") return true;
      if (s === "changes") return (run?.versionNo ?? 1) <= 1;
      if (s === "segments") return segments.length <= 1;
      if (s === "service_lines") return availableLines.length === 0;
      return false;
    },
    [run?.versionNo, segments.length, availableLines.length]
  );
  /** The next visible step in `dir` (+1 forward, -1 back), or null if there is none. */
  const adjacentStep = (dir: 1 | -1): StepId | null => {
    for (let i = stepIndex + dir; i >= 0 && i < WIZARD_STEPS.length; i += dir) {
      if (!stepHidden(WIZARD_STEPS[i].id)) return WIZARD_STEPS[i].id;
    }
    return null;
  };
  const prevStep = adjacentStep(-1);
  const nextStep = adjacentStep(1);

  // Segment first (Ani's principle): when the run first loads and the contract spans more than one line
  // of business, open the Lines of Business step, so LOB scope is settled before diving into a payer's
  // terms. One-shot per run (keyed by id) so it never yanks the reviewer back after they've navigated.
  const autoLandedRunRef = useRef<string | null>(null);
  useEffect(() => {
    if (!run?.id || !run.recommendations?.length) return;
    if (autoLandedRunRef.current === run.id) return;
    // Only CONSUME the one-shot when we actually land. If the step isn't visible yet — a poll caught the
    // recs mid-persist, so a second payer's rec hasn't arrived and segments is momentarily 1 — leave the
    // ref unset so we still land once it does. Consuming it here would block the landing forever.
    if (stepHidden("segments")) return;
    autoLandedRunRef.current = run.id;
    setActiveStep("segments");
  }, [run?.id, run?.recommendations?.length, stepHidden]);

  const [selectedLines, setSelectedLines] = useState<string[] | null>(null);
  useEffect(() => {
    if (selectedLines !== null || availableLines.length === 0) return;
    setSelectedLines(
      availableLines.filter((line) =>
        allRecs.some(
          (r) => serviceLineOf(r) === line && r.status !== "rejected"
        )
      )
    );
  }, [availableLines, allRecs, selectedLines]);

  const handleToggleServiceLine = (line: string) =>
    setSelectedLines((prev) => {
      const current = prev ?? availableLines;
      return current.includes(line)
        ? current.filter((l) => l !== line)
        : [...current, line];
    });

  /**
   * Apply the picker: reject everything in a skipped line, restore everything in a kept one.
   * Rejected recs already drop off the implementation checklist, so a skipped line simply means nobody
   * is ever told to build it. Nothing is deleted, and re-ticking the line puts it back.
   */
  const handleApplyServiceLines = async () => {
    const keep = selectedLines ?? availableLines;
    // Recs with no service line are contract-wide — not a line you can decline, so never touched.
    const inAPickedLine = allRecs.filter((r) => serviceLineOf(r) !== null);
    const toReject = inAPickedLine.filter(
      (r) =>
        !keep.includes(serviceLineOf(r) as string) && r.status !== "rejected"
    );
    const toRestore = inAPickedLine.filter(
      (r) =>
        keep.includes(serviceLineOf(r) as string) && r.status === "rejected"
    );

    try {
      await Promise.all([
        ...toReject.map((r) => reject(r.id).unwrap()),
        ...toRestore.map((r) => restore(r.id).unwrap()),
      ]);
      const skipped = availableLines.filter((l) => !keep.includes(l));
      toastRef?.current?.show({
        severity: "success",
        summary: "Service lines applied",
        detail: skipped.length
          ? `Skipping ${skipped.join(", ")}.`
          : "Modeling every service line.",
        life: 5000,
      });
      setActiveStep("inpatient");
    } catch {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't apply the service-line selection",
        life: 5000,
      });
    }
  };

  // ---- Lines of business (payer segments) — same mechanism as the service-line picker, by payer -------
  const [selectedPayers, setSelectedPayers] = useState<string[] | null>(null);
  useEffect(() => {
    if (selectedPayers !== null || segments.length === 0) return;
    // Default: a segment starts ticked if any of its recs is still live (a fully-rejected one starts off).
    setSelectedPayers(
      segments
        .filter((g) => g.recs.some((r) => r.status !== "rejected"))
        .map((g) => g.key)
    );
  }, [segments, selectedPayers]);

  const handleTogglePayer = (key: string) =>
    setSelectedPayers((prev) => {
      const current = prev ?? segments.map((g) => g.key);
      return current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
    });

  /**
   * Apply the picker: reject every recommendation under a skipped line of business (its contract term,
   * base rate file, rate calc, rules), restore them for a kept one. A rejected rec drops off the
   * checklist, so a skipped line of business is simply never built; re-ticking puts it back. This is the
   * "reject the whole payer" cascade — a contract term never leaves its base rate file / rate calc behind.
   */
  const handleApplySegments = async () => {
    const keep = selectedPayers ?? segments.map((g) => g.key);
    const recKey = new Map<string, string>();
    segments.forEach((g) => g.recs.forEach((r) => recKey.set(r.id, g.key)));
    const toReject = allRecs.filter((r) => {
      const k = recKey.get(r.id);
      return k != null && !keep.includes(k) && r.status !== "rejected";
    });
    const toRestore = allRecs.filter((r) => {
      const k = recKey.get(r.id);
      return k != null && keep.includes(k) && r.status === "rejected";
    });

    try {
      await Promise.all([
        ...toReject.map((r) => reject(r.id).unwrap()),
        ...toRestore.map((r) => restore(r.id).unwrap()),
      ]);
      const skipped = segments.filter((g) => !keep.includes(g.key));
      toastRef?.current?.show({
        severity: "success",
        summary: "Lines of business applied",
        detail: skipped.length
          ? `Skipping ${skipped
              .map((g) => g.payerName ?? `Payer ${g.payerId}`)
              .join(", ")}.`
          : "Modeling every line of business.",
        life: 5000,
      });
      setActiveStep("terms");
    } catch {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't apply the line-of-business selection",
        life: 5000,
      });
    }
  };

  const handleAddInstruction = async (body: string, stepId: StepId) => {
    if (!id) return;
    try {
      await addMessage({ runId: id, body, stepId }).unwrap();
    } catch {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't save that instruction",
        life: 5000,
      });
    }
  };

  const handleDeleteInstruction = async (messageId: string) => {
    if (!id) return;
    try {
      await deleteMessage({ runId: id, messageId }).unwrap();
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't remove it",
        detail: errorDetail(e),
        life: 5000,
      });
    }
  };

  const handleRegenerate = async () => {
    if (!id) return;
    try {
      await regenerate(id).unwrap();
      toastRef?.current?.show({
        severity: "success",
        summary: "Re-deriving the model",
        // Say plainly what will happen, including that her work survives — that is the whole reason
        // it is safe to keep working while this runs.
        detail:
          "This takes a few minutes. Keep reviewing — anything that doesn't change keeps your verdict.",
        life: 7000,
      });
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't regenerate",
        detail: errorDetail(e),
        life: 7000,
      });
    }
  };

  /** Confirm a recommendation, saving the reviewer's inline corrections when they made any. */
  const handleAccept = async (
    recommendationId: string,
    editedModeling?: Record<string, unknown>
  ) => {
    try {
      await accept({ recommendationId, editedModeling }).unwrap();
      // No success toast: the button itself flips to a filled green "Confirmed", which is feedback
      // enough — and the toast landed on top of the "Confirm & continue" button, blocking it.
    } catch (e) {
      // The server re-validates every edit against the same schema the agent's output is held to, so
      // this is where a bad correction (85 instead of 0.85) is caught. Surface its message verbatim.
      const message =
        (e as { data?: { message?: string } })?.data?.message ??
        "Couldn't save this recommendation.";
      toastRef?.current?.show({
        severity: "error",
        summary: "Not saved",
        detail: message,
        life: 8000,
      });
    }
  };

  const handleReject = async (recommendationId: string) => {
    // No success toast, same as confirm: the button flips to a filled "Rejected", and a toast here
    // covered the action buttons the reviewer is working through.
    await reject(recommendationId)
      .unwrap()
      .catch(() => undefined);
  };

  // Everything the model said it still needs, rolled up from the individual recommendations.
  const missingInputs = useMemo(
    () => collectMissingInputs(run?.recommendations ?? []),
    [run]
  );

  // Once a run has produced recommendations it is a finished record: uploading more documents or
  // re-generating would append a second set of recommendations to it. Start a new run instead.
  const finished = isRunFinished(run?.recommendations ?? []);

  const hint = run
    ? generateHint(run.documents.length, run.status, { uploading, finished })
    : null;

  const toast = (
    severity: "success" | "error",
    summary: string,
    detail: string
  ) => toastRef?.current?.show({ severity, summary, detail, life: 5000 });

  // Generate + download the Medicare CSV a base_rate_file recommendation is built from.
  const handleGenerateCsv = async (
    recId: string,
    proposedModeling?: Record<string, unknown>
  ) => {
    setGeneratingCsvId(recId);
    try {
      // `proposedModeling` is the card's unsaved draft — preview it without committing. Omitted → the
      // persisted recommendation.
      const { blob, fileName } = await fetchBaseRateCsv({
        recommendationId: recId,
        proposedModeling,
      }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast("error", "Couldn't generate the base rate CSV", errorDetail(e));
    } finally {
      setGeneratingCsvId(null);
    }
  };

  const handleToggleImplemented = async (
    recId: string,
    implemented: boolean
  ) => {
    // The checkbox flips instantly via the mutation's optimistic cache update; if the save fails it
    // rolls back and we say so.
    try {
      await setImplemented({
        runId: id,
        recommendationId: recId,
        implemented,
      }).unwrap();
    } catch (e) {
      toast("error", "Couldn't save that", errorDetail(e));
    }
  };

  const handleUpload = async (file: File) => {
    const parts = file.name.toLowerCase().split(".");
    const ext = parts.length > 1 ? parts[parts.length - 1] : "";
    if (!ALLOWED_UPLOAD_EXTENSIONS.includes(ext)) {
      toast(
        "error",
        "Unsupported file type",
        `.${
          ext || "?"
        } isn't supported. Allowed: ${ALLOWED_UPLOAD_EXTENSIONS.join(", ")}.`
      );
      return;
    }
    const contentType = file.type || "application/octet-stream";
    // A PUT straight to S3 has no built-in deadline: a stalled connection hangs forever, which is
    // how an upload could sit "in progress" indefinitely with the button stuck disabled.
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), UPLOAD_TIMEOUT_MS);
    try {
      setUploadingFile(file.name);
      const { uploadUrl, documentId } = await presignDoc({
        runId: id,
        fileName: file.name,
        contentType,
      }).unwrap();

      const res = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": contentType },
        signal: abort.signal,
      });
      if (!res.ok) throw new Error(`S3 rejected the upload (${res.status}).`);

      // Don't trust the PUT alone — ask the API to HEAD the object. This is what turns a silently
      // failed upload into an error you can see, instead of a run that dies 20 minutes later.
      await confirmDoc({ runId: id, documentId }).unwrap();
      toast("success", "Document uploaded", file.name);
    } catch (e) {
      const detail =
        e instanceof DOMException && e.name === "AbortError"
          ? `Upload timed out after ${
              UPLOAD_TIMEOUT_MS / 1000
            }s. Check your connection and try again.`
          : errorDetail(e);
      toast("error", `Couldn't upload ${file.name}`, detail);
    } finally {
      clearTimeout(timer);
      setUploadingFile(null);
    }
  };

  /**
   * A recommendation-scoped instruction. It goes into the SAME queue as the step-level box, so there is
   * one mechanism for "tell the agent something", not two that behave differently.
   *
   * The eval signal we used to get from the override form is not lost: an inline edit already records
   * "the agent said X, the human corrected it to Y" (`originalModeling`) with no form to fill in, and
   * this instruction text is itself the record of what the agent got wrong.
   */
  const submitOverride = async () => {
    if (!overrideTarget || !id) return;
    try {
      await addMessage({
        runId: id,
        body: overrideReason.trim(),
        stepId: activeStep,
        recommendationId: overrideTarget.id,
      }).unwrap();
      setOverrideTarget(null);
      setOverrideReason("");
      toastRef?.current?.show({
        severity: "success",
        summary: "Instruction queued",
        detail: "It will be applied the next time you Regenerate.",
        life: 5000,
      });
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't queue that instruction",
        detail: errorDetail(e),
        life: 5000,
      });
    }
  };

  return (
    <div className="p-4">
      <Button
        label="Back to runs"
        icon="pi pi-arrow-left"
        className="p-button-text p-button-sm mb-2 pl-0"
        onClick={() => navigate("/contract-model-drafts")}
      />

      {!run ? (
        <p className="text-700">Loading run…</p>
      ) : (
        <>
          {/*
            The heading is the SUBJECT of the run — which provider's contract with which payer — not
            "Run d7ecc1cf". A truncated uuid told the reviewer nothing about what they had opened, and
            the sentence underneath then repeated the provider and payer a third time, after the tags.
            The id is still here because it is what you quote in a bug report; it is just not the title.
          */}
          <div className="flex align-items-center gap-2 mb-1 flex-wrap">
            <h1 className="m-0">
              {run.providerName && run.payerName
                ? `${run.providerName} × ${run.payerName}`
                : runDisplayName(run)}
            </h1>
            <Tag
              value={runStatusLabel(run.status)}
              severity={runStatusSeverity(run.status)}
            />
          </div>
          <div className="flex align-items-center gap-2 mb-3 text-sm text-color-secondary flex-wrap">
            {siteTierLabel(run.siteTier) ? (
              <span>{siteTierLabel(run.siteTier)} rates</span>
            ) : null}
            {siteTierLabel(run.siteTier) ? <span>·</span> : null}
            <span title={run.id}>Run {run.id.slice(0, 8)}</span>
            {run.versionNo > 1 ? (
              <>
                <span>·</span>
                <span>Version {run.versionNo}</span>
              </>
            ) : null}
          </div>

          {/*
            The steering the reviewer gave the model at generation time — "model Network P only", etc.
            Once a model exists the input box is hidden (it only shapes the initial read), so without
            this the instructions would be invisible. It's what tells you which network/slice this run
            represents when one document covers several.
          */}
          {run.generationInstructions ? (
            <div className="border-1 surface-border border-round p-3 mb-4 surface-card">
              <div className="text-sm font-medium text-color-secondary mb-1">
                <i className="pi pi-compass mr-2" />
                Instructions given to the AI
              </div>
              <div className="text-sm white-space-pre-wrap">
                {run.generationInstructions}
              </div>
            </div>
          ) : null}

          {missingInputs.length > 0 ? (
            <MissingInputsPanel items={missingInputs} />
          ) : null}

          <div className="surface-card border-1 surface-border border-round p-3 mb-4">
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="font-semibold">
                Documents ({run.documents.length})
              </span>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={ALLOWED_UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(",")}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  e.target.value = "";
                }}
              />
              <Button
                // A finished run is no longer locked. Ani asked for exactly this: while reviewing the
                // inpatient step she realises she has another document, and she must be able to add it
                // without throwing the run away. It does NOT auto-regenerate — the run just goes stale
                // and the Regenerate bar appears.
                label={uploading ? "Uploading…" : "Upload document"}
                icon="pi pi-upload"
                className="p-button-outlined p-button-sm"
                loading={uploading}
                disabled={uploading || isProcessing(run.status)}
                onClick={() => fileInputRef.current?.click()}
              />
            </div>

            {run.documents.length === 0 && !uploading ? (
              <p className="text-700 text-sm m-0">
                Add the signed contract and any rate workbooks it refers to.
                PDF, Word or Excel.
              </p>
            ) : (
              <ul className="list-none p-0 m-0">
                {run.documents.map((d) => (
                  <li key={d.id} className="text-sm py-1">
                    <i className="pi pi-file mr-2 text-700" />
                    {d.fileName}
                  </li>
                ))}
                {uploadingFile ? (
                  <li className="text-sm py-1 text-700">
                    <i className="pi pi-spin pi-spinner mr-2" />
                    Uploading {uploadingFile}…
                  </li>
                ) : null}
              </ul>
            )}

            {/*
              Hidden while generating: the progress panel below is the thing to look at, and a disabled
              "Generating…" button next to it just said the same word twice without doing anything.
            */}
            {!isProcessing(run.status) ? (
              <div className="mt-3">
                {/*
                  Steering for the model, before it runs. This is how ONE contract covering several plan
                  networks / LOBs becomes several runs: each run says which slice it wants. Optional, and
                  general — it also takes "the office column is the one we want", "ignore the behavioral-
                  health exhibit". Hidden once a model exists, since it only shapes the initial read;
                  after that, corrections go through the per-recommendation instructions and Regenerate.
                */}
                {!finished ? (
                  <div className="mb-3">
                    <label
                      htmlFor="gen-instructions"
                      className="block text-sm font-medium mb-1"
                    >
                      Instructions for the AI{" "}
                      <span className="text-color-secondary font-normal">
                        (optional)
                      </span>
                    </label>
                    <InputTextarea
                      id="gen-instructions"
                      className="w-full"
                      autoResize
                      rows={2}
                      disabled={generating || uploading}
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="e.g. Model Network P only. Use the office column, not facility."
                    />
                    <small className="text-color-secondary">
                      Scope the run when one document covers several networks or
                      lines of business. The model follows this over its own
                      reading of the document.
                    </small>
                  </div>
                ) : null}

                <div className="flex align-items-center gap-2">
                  <Button
                    label={finished ? "Model generated" : "Generate model"}
                    icon={finished ? "pi pi-check" : "pi pi-bolt"}
                    disabled={
                      generating ||
                      finished ||
                      uploading ||
                      run.documents.length === 0
                    }
                    onClick={() =>
                      runGeneration({ runId: run.id, instructions })
                    }
                  />
                  {hint ? (
                    <span className="text-700 text-sm">{hint}</span>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          {isProcessing(run.status) ? (
            <GenerationProgress
              status={run.status}
              updatedAt={run.updatedAt}
              documentCount={run.documents.length}
            />
          ) : null}

          {/* While generating, the progress panel above is the status — don't also say "nothing yet". */}
          {run.recommendations.length === 0 && !isProcessing(run.status) ? (
            <p className="text-700">
              Nothing modeled yet. Add the contract documents above, then
              generate.
            </p>
          ) : null}

          {run.recommendations.length > 0 ? (
            <div className="grid mt-2">
              <div className="col-12">
                <PendingChangesBar
                  run={run}
                  regenerating={regenerating}
                  onRegenerate={handleRegenerate}
                />
              </div>
              {providerHint && providerHint.otherProviders.length > 0 ? (
                <div className="col-12">
                  <div
                    className="border-round p-3 border-1 text-sm"
                    style={{
                      background: "var(--yellow-50, #fffbeb)",
                      borderColor: "var(--yellow-300, #fcd34d)",
                    }}
                  >
                    <div className="flex align-items-start gap-2">
                      <i
                        className="pi pi-exclamation-triangle mt-1"
                        style={{ color: "var(--yellow-700, #a16207)" }}
                      />
                      <div className="line-height-3">
                        <span className="font-semibold">
                          This contract looks modeled under a different
                          provider.
                        </span>{" "}
                        Nothing is in production for{" "}
                        <strong>{run.providerName ?? "this provider"}</strong>
                        {run.payerName ? ` + ${run.payerName}` : ""}, but{" "}
                        {providerHint.otherProviders.length === 1
                          ? "this contract already exists under "
                          : "this contract already exists under these providers: "}
                        {providerHint.otherProviders
                          .map((p) => p.name)
                          .join(", ")}
                        . If that&rsquo;s the right entity, start a new run
                        there &mdash; verification is scoped to this run&rsquo;s
                        provider, so it will keep reading as &ldquo;not in
                        production&rdquo; here.
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="col-12 md:col-3">
                <StepRail
                  recs={run.recommendations}
                  activeStep={activeStep}
                  onSelect={setActiveStep}
                  unlockedThrough={unlockedThrough}
                  showChanges={run.versionNo > 1}
                  hasServiceLines={availableLines.length > 0}
                  hasSegments={segments.length > 1}
                />
              </div>

              <div className="col-12 md:col-9">
                {activeStep === "changes" && (
                  <ChangesScreen run={run} onOpen={handleViewDetails} />
                )}

                {activeStep === "build" && (
                  <ImplementationChecklist
                    recs={run.recommendations}
                    verification={verification}
                    loadingLive={loadingLive}
                    onRefresh={refetchVerification}
                    onToggle={handleToggleImplemented}
                    onViewDetails={handleViewDetails}
                  />
                )}

                {activeStep !== "build" && activeStep !== "changes" && (
                  <>
                    <h3 className="mt-0 mb-1">{activeStepMeta.title}</h3>
                    <p className="mt-0 mb-3 text-700">{activeStepMeta.blurb}</p>

                    {activeStep === "segments" && (
                      <LineOfBusinessPicker
                        segments={segments}
                        selected={selectedPayers ?? segments.map((g) => g.key)}
                        busy={busy}
                        onToggle={handleTogglePayer}
                        onApply={handleApplySegments}
                      />
                    )}

                    {activeStep === "service_lines" && (
                      <ServiceLinePicker
                        recs={run.recommendations}
                        selected={selectedLines ?? availableLines}
                        busy={busy}
                        onToggle={handleToggleServiceLine}
                        onApply={handleApplyServiceLines}
                      />
                    )}

                    {activeStep !== "service_lines" &&
                      activeStep !== "segments" &&
                      stepRecs.length === 0 && (
                        <p className="text-700">
                          Nothing in this step — the contract didn&rsquo;t
                          produce any {activeStepMeta.title.toLowerCase()}.
                        </p>
                      )}

                    {/*
                      Inpatient and outpatient read as a RATE GRID, not as cards (Ani's v0 steps 2-3).

                      These two steps are where a facility contract's rate sheet lives, and a rate sheet
                      is a table: one row per priced line, in the contract's own vocabulary (revenue
                      codes, DRGs), so you can see at a glance that two lines disagree about methodology
                      or that a carve-out is missing. Rendered as cards, a 16-line outpatient grid is 16
                      things to page through and none of that is visible.

                      Every other step keeps the per-payer card accordion below — a contract term or an
                      MPDR ruleset is a thing you read in full, not a row you scan.
                    */}
                    {isRulesStep && stepRecs.length > 0 && (
                      <AdjustmentRulesPanel
                        recs={stepRecs}
                        busy={busy}
                        focusRecId={focusRecId}
                        onAccept={(rec) => handleAccept(rec.id)}
                        onReject={(rec) => handleReject(rec.id)}
                        renderDetail={(rec) => (
                          <RecommendationCard
                            rec={rec}
                            busy={busy}
                            resolve={resolveIdName}
                            payers={payerOptions}
                            baseRateFiles={baseRateFileNames}
                            baseRateFileOptions={baseRateFileOptions}
                            existingBaseRateFiles={existingBaseRateFiles}
                            match={verification ? verification[rec.id] : null}
                            focused={focusRecId === rec.id}
                            onGenerateCsv={handleGenerateCsv}
                            generatingCsv={generatingCsvId === rec.id}
                            onAccept={(edited) => handleAccept(rec.id, edited)}
                            onReject={() => handleReject(rec.id)}
                            onOverride={() => setOverrideTarget(rec)}
                          />
                        )}
                      />
                    )}

                    {isGridStep && stepRecs.length > 0 && (
                      <RateGrid
                        recs={stepRecs}
                        focusRecId={focusRecId}
                        renderDetail={(rec) => (
                          <RecommendationCard
                            rec={rec}
                            busy={busy}
                            resolve={resolveIdName}
                            payers={payerOptions}
                            baseRateFiles={baseRateFileNames}
                            baseRateFileOptions={baseRateFileOptions}
                            existingBaseRateFiles={existingBaseRateFiles}
                            match={verification ? verification[rec.id] : null}
                            focused={focusRecId === rec.id}
                            onGenerateCsv={handleGenerateCsv}
                            generatingCsv={generatingCsvId === rec.id}
                            onAccept={(edited) => handleAccept(rec.id, edited)}
                            onReject={() => handleReject(rec.id)}
                            onOverride={() => setOverrideTarget(rec)}
                          />
                        )}
                      />
                    )}

                    {/*
                      Grouped by PAYER, in an accordion.

                      A contract is usually with several payers, and the agent emits per-payer
                      constructs in generation order — so a flat list interleaved Aetna's contract term,
                      the Medicare Advantage link, and Aetna Medicare Advantage's contract term, which
                      read as three unrelated things and left the reviewer wondering whether two
                      identical-looking cards were duplicates. They were not: they were different payers.

                      One group per payer, so "everything for Aetna" is one thing you open.
                    */}
                    {activeStep !== "service_lines" &&
                      !isGridStep &&
                      !isRulesStep &&
                      stepRecs.length > 0 && (
                        <Accordion
                          multiple
                          // Auto-open when there is only one group — making her click to see the only
                          // thing on the page would be silly. With several payers they start closed, so
                          // the step opens as a short, readable index instead of a wall of forms.
                          activeIndex={stepPayerGroups.length === 1 ? [0] : []}
                        >
                          {stepPayerGroups.map((group) => {
                            const unreviewed = group.recs.filter(
                              (r) =>
                                r.status === "pending" ||
                                r.status === "needs_input"
                            ).length;
                            return (
                              <AccordionTab
                                key={group.key}
                                header={
                                  <div className="flex align-items-center gap-2 w-full">
                                    <i
                                      className={
                                        group.payerId != null
                                          ? "pi pi-building"
                                          : "pi pi-sitemap"
                                      }
                                    />
                                    <span className="font-semibold">
                                      {group.payerId != null
                                        ? group.payerName ??
                                          `Payer ${group.payerId}`
                                        : "Contract-wide (all payers)"}
                                    </span>
                                    <span className="text-color-secondary text-sm">
                                      {group.recs.length}{" "}
                                      {group.recs.length === 1
                                        ? "item"
                                        : "items"}
                                    </span>
                                    {unreviewed > 0 ? (
                                      <Tag
                                        className="ml-auto"
                                        severity="warning"
                                        value={`${unreviewed} to review`}
                                      />
                                    ) : (
                                      <Tag
                                        className="ml-auto"
                                        severity="success"
                                        value="Reviewed"
                                      />
                                    )}
                                  </div>
                                }
                              >
                                {group.recs.map((rec) => (
                                  <RecommendationCard
                                    key={rec.id}
                                    rec={rec}
                                    busy={busy}
                                    resolve={resolveIdName}
                                    payers={payerOptions}
                                    baseRateFiles={baseRateFileNames}
                                    baseRateFileOptions={baseRateFileOptions}
                                    existingBaseRateFiles={
                                      existingBaseRateFiles
                                    }
                                    match={
                                      verification ? verification[rec.id] : null
                                    }
                                    focused={focusRecId === rec.id}
                                    onGenerateCsv={handleGenerateCsv}
                                    generatingCsv={generatingCsvId === rec.id}
                                    onAccept={(edited) =>
                                      handleAccept(rec.id, edited)
                                    }
                                    onReject={() => handleReject(rec.id)}
                                    onOverride={() => setOverrideTarget(rec)}
                                  />
                                ))}
                              </AccordionTab>
                            );
                          })}
                        </Accordion>
                      )}

                    <InstructionPanel
                      stepId={activeStep}
                      messages={run.messages}
                      busy={busy}
                      regenerating={regenerating}
                      onAdd={handleAddInstruction}
                      onDelete={handleDeleteInstruction}
                      onRegenerate={handleRegenerate}
                    />

                    <div className="flex justify-content-between align-items-center mt-4 pt-3 border-top-1 surface-border">
                      <Button
                        label="Back"
                        icon="pi pi-arrow-left"
                        className="p-button-text"
                        disabled={prevStep === null}
                        onClick={() => prevStep && setActiveStep(prevStep)}
                      />
                      <div className="flex align-items-center gap-3">
                        {unresolvedHere > 0 ? (
                          <small className="text-color-secondary">
                            {unresolvedHere} still to review
                          </small>
                        ) : null}
                        <Button
                          // Gated on `pending` only (Ani's v0). A `needs_input` item cannot be built
                          // until someone fixes data the agent got wrong — blocking on it would freeze
                          // the run with no way past, so it stays loud rather than becoming a dead end.
                          label="Confirm & continue"
                          icon="pi pi-arrow-right"
                          iconPos="right"
                          disabled={nextStep === null || currentStepBlocks}
                          tooltip={
                            currentStepBlocks
                              ? "Accept, reject or give feedback on every item in this step first"
                              : undefined
                          }
                          onClick={() => nextStep && setActiveStep(nextStep)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </>
      )}

      {/*
        A recommendation-scoped instruction. It joins the SAME queue as the step-level instruction box —
        there is one mechanism, not two — and is applied on the next Regenerate.
      */}
      <Dialog
        header={`Request a change to this ${
          overrideTarget ? componentTypeLabel(overrideTarget.componentType) : ""
        }`}
        visible={!!overrideTarget}
        style={{ width: "40rem" }}
        onHide={() => setOverrideTarget(null)}
      >
        <div className="flex flex-column gap-3">
          <p className="text-700 text-sm m-0">
            Tell the agent what it got wrong. This joins your{" "}
            <strong>Requested changes</strong> and is applied when you press{" "}
            <strong>Regenerate</strong> — <strong>nothing changes now</strong>.
            It&rsquo;s the same queue as the &ldquo;Requested changes&rdquo; box
            at the bottom of each step; this one is just already tied to this
            recommendation.
          </p>
          <p className="text-600 text-sm m-0">
            If you already know the right value, it is quicker to just edit the
            fields and press Confirm.
          </p>
          <InputTextarea
            id="rec-instruction"
            className="w-full"
            rows={4}
            autoResize
            placeholder="e.g. This should be a case rate, not a per diem — see the Rate Schedule on page 12."
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
          />
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setOverrideTarget(null)}
            />
            <Button
              label="Add to Requested changes"
              icon="pi pi-plus"
              disabled={busy || !overrideReason.trim()}
              onClick={submitOverride}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
